import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import {
    BalanceOutlined,
    CalendarTodayOutlined,
    EmojiEventsOutlined,
    GradeOutlined,
    HubRounded,
    ShieldOutlined,
    SouthOutlined,
} from "@mui/icons-material";
import {
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
} from "recharts";
import { colors } from "../../theme/colors";
import { EmptyDataState } from "../common/EmptyDataState";
import { TooltipShell } from "../common/Tooltip";
import { REPUTATION_DIMENSIONS } from "./ReputationDimensionCard";

const CHART_NO_FOCUS_SX = {
    outline: "none",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    "&:focus": { outline: "none" },
    "&:focus-visible": { outline: "none" },
    "& .recharts-wrapper": { outline: "none !important" },
    "& .recharts-surface": { outline: "none !important" },
    "& svg": { outline: "none" },
    "& svg:focus": { outline: "none" },
    "& *:focus": { outline: "none" },
    "& *:focus-visible": { outline: "none" },
};

function buildRadarAxes(dimensions) {
    return REPUTATION_DIMENSIONS.map(({ key, label }) => {
        const score = dimensions?.[key]?.score ?? 0;
        return {
            subject: label,
            value: score,
            display: `${score}/100`,
        };
    });
}

function getScoredDimensions(dimensions) {
    return REPUTATION_DIMENSIONS.map(({ key, label }) => ({
        key,
        label,
        score: dimensions?.[key]?.score,
    })).filter((entry) => Number.isFinite(entry.score));
}

function getDimensionInsights(dimensions) {
    const scored = getScoredDimensions(dimensions);
    if (!scored.length) {
        return null;
    }

    const scores = scored.map((entry) => entry.score);
    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);

    if (minScore === maxScore) {
        return {
            isBalanced: true,
            score: minScore,
        };
    }

    return {
        isBalanced: false,
        highest: scored.find((entry) => entry.score === maxScore),
        lowest: scored.find((entry) => entry.score === minScore),
    };
}

function formatReportsValue(count) {
    if (!count) {
        return "0 No recent reports";
    }

    return count === 1 ? "1 report" : `${count} reports`;
}

function RadarAxisTick({ x, y, payload }) {
    return (
        <g transform={`translate(${x},${y})`}>
            <foreignObject x={-52} y={-10} width={104} height={22}>
                <Typography
                    component="div"
                    sx={{
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        color: colors.text.secondary,
                        lineHeight: 1.2,
                        textAlign: "center",
                    }}
                >
                    {payload.value}
                </Typography>
            </foreignObject>
        </g>
    );
}

function RadarTooltip({ active, payload }) {
    if (!active || !payload?.length) {
        return null;
    }

    const point = payload[0]?.payload;
    if (!point) {
        return null;
    }

    return (
        <TooltipShell sx={{ minWidth: "8.5rem" }}>
            <Typography
                sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: colors.text.muted,
                    lineHeight: 1,
                }}
            >
                {point.subject}
            </Typography>
            <Typography
                sx={{
                    mt: "0.35rem",
                    fontSize: "0.92rem",
                    fontWeight: 800,
                    color: colors.text.primary,
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                {point.display}
            </Typography>
        </TooltipShell>
    );
}

function InsightCard({ icon: Icon, iconColor, label, title, value, valueColor = colors.primary.main }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "0.75rem",
                px: "0.9rem",
                py: "0.8rem",
                borderRadius: "10px",
                backgroundColor: "rgba(255,255,255,0.025)",
                border: "1px solid rgba(255,255,255,0.05)",
                minWidth: 0,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: "0.65rem", flex: 1, minWidth: 0 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "2rem",
                        height: "2rem",
                        borderRadius: "8px",
                        flexShrink: 0,
                        backgroundColor: `${iconColor}14`,
                        color: iconColor,
                    }}
                >
                    <Icon sx={{ fontSize: "1.05rem" }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: "0.58rem",
                            fontWeight: 700,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.38)",
                            lineHeight: 1,
                        }}
                    >
                        {label}
                    </Typography>
                    <Typography
                        sx={{
                            mt: "0.2rem",
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            color: "rgba(255,255,255,0.78)",
                            lineHeight: 1.35,
                        }}
                    >
                        {title}
                    </Typography>
                </Box>
            </Box>
            <Typography
                sx={{
                    fontSize: "0.82rem",
                    fontWeight: 800,
                    color: valueColor,
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                    flexShrink: 0,
                    pt: "0.1rem",
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

InsightCard.propTypes = {
    icon: PropTypes.elementType.isRequired,
    iconColor: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    valueColor: PropTypes.string,
};

function FooterStat({ icon: Icon, label, value }) {
    return (
        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.4rem",
                    maxWidth: "100%",
                }}
            >
                <Box
                    sx={{
                        width: "1rem",
                        height: "1rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <Icon sx={{ fontSize: "0.9rem", color: colors.primary.main }} />
                </Box>
                <Typography
                    sx={{
                        fontSize: "0.58rem",
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.34)",
                        lineHeight: 1,
                    }}
                >
                    {label}
                </Typography>
            </Box>
            <Typography
                sx={{
                    mt: "0.35rem",
                    fontSize: "0.84rem",
                    fontWeight: 700,
                    color: colors.text.primary,
                    lineHeight: 1.3,
                    fontVariantNumeric: "tabular-nums",
                    textAlign: "center",
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

FooterStat.propTypes = {
    icon: PropTypes.elementType.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
};

export default function ReputationRadarChart({
    dimensions = null,
    overallScore = null,
    reportsCount = 0,
    lastUpdatedLabel = "—",
}) {
    const radarAxes = useMemo(
        () => (dimensions ? buildRadarAxes(dimensions) : []),
        [dimensions],
    );

    const insights = useMemo(
        () => (dimensions ? getDimensionInsights(dimensions) : null),
        [dimensions],
    );

    const averageScore = useMemo(() => {
        const scored = getScoredDimensions(dimensions ?? {});
        if (!scored.length) {
            return overallScore;
        }

        const total = scored.reduce((sum, entry) => sum + entry.score, 0);
        return Math.round(total / scored.length);
    }, [dimensions, overallScore]);

    if (!dimensions) {
        return (
            <EmptyDataState
                title="No breakdown available"
                icon={HubRounded}
                minHeight="18rem"
                embedded
            >
                Reputation dimensions appear after enough rated activity
            </EmptyDataState>
        );
    }

    const insightCards = insights?.isBalanced ? (
        <InsightCard
            icon={BalanceOutlined}
            iconColor={colors.primary.main}
            label="Balanced"
            title="All categories share the same score"
            value={`${insights.score}/100`}
        />
    ) : (
        <>
            <InsightCard
                icon={EmojiEventsOutlined}
                iconColor={colors.primary.main}
                label="Highest"
                title={insights?.highest?.label ?? "—"}
                value={`${insights?.highest?.score ?? "—"}/100`}
            />
            <InsightCard
                icon={SouthOutlined}
                iconColor={colors.accent.main}
                label="Lowest"
                title={insights?.lowest?.label ?? "—"}
                value={`${insights?.lowest?.score ?? "—"}/100`}
                valueColor={colors.accent.main}
            />
        </>
    );

    return (
        <Box
            sx={{
                position: "relative",
                zIndex: 2,
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                height: "100%",
            }}
        >
            <Box>
                <Typography
                    sx={{
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: colors.text.primary,
                        lineHeight: 1,
                    }}
                >
                    Reputation breakdown
                </Typography>
                <Typography
                    sx={{
                        mt: "0.45rem",
                        fontSize: "0.78rem",
                        fontWeight: 500,
                        color: "rgba(255,255,255,0.46)",
                        lineHeight: 1.45,
                    }}
                >
                    Visual representation of your reputation across all categories.
                </Typography>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: "1rem",
                    flex: 1,
                    minHeight: 0,
                    alignItems: { md: "stretch" },
                }}
            >
                <Box
                    sx={{
                        flex: "1 1 0",
                        minWidth: 0,
                        minHeight: { xs: "16rem", md: "17rem" },
                        maxHeight: { md: "19rem" },
                        ...CHART_NO_FOCUS_SX,
                    }}
                >
                    <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                        <RadarChart cx="50%" cy="50%" outerRadius="78%" data={radarAxes}>
                            <PolarGrid
                                gridType="polygon"
                                stroke="rgba(255,255,255,0.07)"
                            />
                            <PolarAngleAxis
                                dataKey="subject"
                                tick={RadarAxisTick}
                                tickLine={false}
                            />
                            <PolarRadiusAxis
                                angle={90}
                                domain={[0, 100]}
                                tick={false}
                                axisLine={false}
                            />
                            <RechartsTooltip content={<RadarTooltip />} />
                            <Radar
                                dataKey="value"
                                name="You"
                                stroke={colors.primary.main}
                                fill={colors.primary.main}
                                fillOpacity={0.18}
                                strokeWidth={2}
                                dot={{
                                    r: 3,
                                    fill: colors.accent.main,
                                    stroke: colors.primary.main,
                                    strokeWidth: 1,
                                }}
                            />
                        </RadarChart>
                    </ResponsiveContainer>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.65rem",
                        flex: { md: "0 0 18rem" },
                        width: { xs: "100%", md: "18rem" },
                        minWidth: { md: "18rem" },
                    }}
                >
                    {insightCards}
                </Box>
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    gap: "0.65rem",
                    pt: "1rem",
                    borderTop: "1px solid rgba(255,255,255,0.05)",
                }}
            >
                <FooterStat
                    icon={GradeOutlined}
                    label="Average score"
                    value={`${averageScore ?? "—"} / 100`}
                />
                <FooterStat
                    icon={ShieldOutlined}
                    label="Reports (3mo)"
                    value={formatReportsValue(reportsCount)}
                />
                <FooterStat
                    icon={CalendarTodayOutlined}
                    label="Last updated"
                    value={lastUpdatedLabel}
                />
            </Box>
        </Box>
    );
}

ReputationRadarChart.propTypes = {
    dimensions: PropTypes.object,
    overallScore: PropTypes.number,
    reportsCount: PropTypes.number,
    lastUpdatedLabel: PropTypes.string,
};
